class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.rootLink = this.querySelector('.berndorf-header-nav__root-link');
    this.backdrop = this.header?.querySelector('[data-mega-menu-backdrop]');
    this.closeTimer = null;
    this.rootLinkWasOpenOnPointerDown = false;
    this.onWindowResize = this.onWindowResize.bind(this);

    this.addEventListener('mouseenter', this.onMouseEnter.bind(this));
    this.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    this.addEventListener('focusin', this.onFocusIn.bind(this));
    this.addEventListener('keydown', this.onKeyDown.bind(this));
    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    this.rootLink?.addEventListener('pointerdown', () => {
      this.rootLinkWasOpenOnPointerDown = this.mainDetailsToggle.open;
    });
    this.rootLink?.addEventListener('click', this.onRootLinkClick.bind(this));
    window.addEventListener('resize', this.onWindowResize);

    if (this.backdrop && !this.backdrop.hasAttribute('data-menu-listener-bound')) {
      this.backdrop.setAttribute('data-menu-listener-bound', '');
      this.backdrop.addEventListener('click', () => {
        document.querySelectorAll('header-menu').forEach((menu) => menu.close());
      });
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onWindowResize);
  }

  onWindowResize() {
    if (!this.mainDetailsToggle.open) {
      this.resetSimpleDropdownPosition();
      this.resetFeaturedMegaMenuPosition();
      return;
    }

    this.positionSimpleDropdown();
    this.positionFeaturedMegaMenu();
  }

  shouldUseHoverInteraction() {
    return (
      window.matchMedia('(min-width: 1060px)').matches &&
      window.matchMedia('(hover: hover)').matches &&
      window.matchMedia('(pointer: fine)').matches
    );
  }

  onRootLinkClick(event) {
    if (this.shouldUseHoverInteraction() || event.detail === 0 || this.rootLinkWasOpenOnPointerDown) return;

    event.preventDefault();
    this.closeSiblingMenus();
    this.open();
  }

  onSummaryClick(event) {
    if (!this.shouldUseHoverInteraction()) return;
    if (event.detail === 0) return;

    event.preventDefault();
    requestAnimationFrame(() => {
      this.summary.setAttribute('aria-expanded', this.mainDetailsToggle.hasAttribute('open'));
    });
  }

  onMouseEnter() {
    if (!this.shouldUseHoverInteraction()) return;

    clearTimeout(this.closeTimer);
    this.closeSiblingMenus();
    this.open();
  }

  onMouseLeave() {
    if (!this.shouldUseHoverInteraction()) return;

    clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(() => {
      this.close();
    }, 120);
  }

  onFocusIn(event) {
    if (event.target !== this.rootLink && event.target !== this.summary) return;

    clearTimeout(this.closeTimer);
    this.closeSiblingMenus();
    this.open();
  }

  onKeyDown(event) {
    if (event.key !== 'Escape' || !this.mainDetailsToggle.open) return;

    event.preventDefault();
    this.close();
    this.rootLink?.focus();
  }

  closeSiblingMenus() {
    document.querySelectorAll('header-menu details[open]').forEach((openDetails) => {
      if (openDetails === this.mainDetailsToggle) return;

      const menu = openDetails.closest('header-menu');
      if (menu) {
        menu.close();
      } else {
        openDetails.removeAttribute('open');
        openDetails.querySelector('summary')?.setAttribute('aria-expanded', false);
      }
    });
  }

  isSimpleDropdown() {
    return this.content?.classList.contains('berndorf-simple-menu__content');
  }

  getSimpleDropdownHandle() {
    const summaryId = this.summary?.id || '';

    if (!summaryId.startsWith('HeaderMenu-')) {
      return '';
    }

    return summaryId.replace('HeaderMenu-', '');
  }

  getSimpleDropdownOffset() {
    const handle = this.getSimpleDropdownHandle();

    if (handle === 'uber-uns') {
      return -18;
    }

    return 0;
  }

  resetSimpleDropdownPosition() {
    if (!this.isSimpleDropdown()) return;

    this.content.style.removeProperty('--berndorf-simple-dropdown-left');
  }

  isFeaturedMegaMenu() {
    return this.mainDetailsToggle?.classList.contains('mega-menu--featured');
  }

  resetFeaturedMegaMenuPosition() {
    if (!this.isFeaturedMegaMenu()) return;

    this.content.style.removeProperty('--berndorf-featured-menu-left');
    this.content.style.removeProperty('--berndorf-featured-menu-top');
    this.content.style.removeProperty('--berndorf-menu-hover-bridge');
  }

  positionFeaturedMegaMenu() {
    if (!this.isFeaturedMegaMenu()) {
      this.resetFeaturedMegaMenuPosition();
      return;
    }

    const nav = this.closest('.berndorf-header-nav');
    if (!nav) return;

    const navRect = nav.getBoundingClientRect();
    const headerRect = this.header.getBoundingClientRect();
    const contentWidth = this.content.getBoundingClientRect().width || this.content.scrollWidth;
    const centeredLeft = (window.innerWidth - contentWidth) / 2 - navRect.left;
    const contentTop = headerRect.bottom - navRect.top;
    // Keep the trigger and panel connected for slow pointer movement.
    const hoverBridge = Math.max(0, headerRect.bottom - this.getBoundingClientRect().bottom);

    this.content.style.setProperty('--berndorf-featured-menu-left', `${Math.round(centeredLeft)}px`);
    // Unrounded: the header bottom sits on a fractional offset, rounding lets the panel overlap it.
    this.content.style.setProperty('--berndorf-featured-menu-top', `${contentTop}px`);
    this.content.style.setProperty('--berndorf-menu-hover-bridge', `${Math.ceil(hoverBridge)}px`);
  }

  positionSimpleDropdown() {
    if (!this.isSimpleDropdown() || !this.shouldUseHoverInteraction()) {
      this.resetSimpleDropdownPosition();
      return;
    }

    const nav = this.closest('.berndorf-header-nav');
    if (!nav) return;

    const navRect = nav.getBoundingClientRect();
    const summaryRect = this.summary.getBoundingClientRect();
    const contentRect = this.content.getBoundingClientRect();
    const contentWidth = contentRect.width || this.content.scrollWidth;
    const preferredLeft = summaryRect.left - navRect.left - 10 + this.getSimpleDropdownOffset();
    const maxLeft = Math.max(navRect.width - contentWidth, 0);
    const clampedLeft = Math.min(Math.max(preferredLeft, 0), maxLeft);

    this.content.style.setProperty('--berndorf-simple-dropdown-left', `${Math.round(clampedLeft)}px`);
  }

  open() {
    if (this.mainDetailsToggle.hasAttribute('open')) return;

    this.mainDetailsToggle.setAttribute('open', '');
    this.setExpandedState(true);
    requestAnimationFrame(() => {
      this.positionSimpleDropdown();
      this.positionFeaturedMegaMenu();
    });
  }

  close() {
    clearTimeout(this.closeTimer);
    super.close();
    this.setExpandedState(false);
  }

  setExpandedState(expanded) {
    this.summary?.setAttribute('aria-expanded', expanded);
    this.rootLink?.setAttribute('aria-expanded', expanded);
  }

  syncHeaderMenuState() {
    if (!this.header) return;

    const openMenus = this.header.querySelectorAll('header-menu details[open]');
    const openFeaturedMenus = this.header.querySelectorAll('header-menu details.mega-menu--featured[open]');
    const hasOpenFeaturedMenu = openFeaturedMenus.length > 0;

    this.header.preventHide = openMenus.length > 0;
    this.header.classList.toggle('is-mega-menu-open', hasOpenFeaturedMenu);
    this.backdrop?.setAttribute('aria-hidden', hasOpenFeaturedMenu ? 'false' : 'true');
  }

  onToggle() {
    super.onToggle();
    if (!this.header) return;
    this.setExpandedState(this.mainDetailsToggle.open);

    if (this.mainDetailsToggle.open) {
      this.positionSimpleDropdown();
      this.positionFeaturedMegaMenu();
    } else {
      this.resetSimpleDropdownPosition();
      this.resetFeaturedMegaMenuPosition();
    }

    this.syncHeaderMenuState();

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
